import { useTranslation } from "react-i18next";
import { Camera, Sparkles } from "lucide-react";
import { useToast } from "@/features/toast";
import { cn } from "@/lib/utils";
import { useProfile } from "../hooks/use-profile";
import { useTryOnPhotos } from "../hooks/use-try-on-photos";

/**
 * Identity Section
 * Persona hero: avatar + name/email in a single modern composition
 */
export const IdentitySection: React.FC = () => {
  const { t } = useTranslation();
  const { error } = useToast();
  const {
    user,
    userImage,
    userInitials,
    formData,
    updateFormData,
    setAvatarPreview,
    setSelectedFile,
    isUploadingAvatar,
  } = useProfile();
  const { photos: tryOnPhotos } = useTryOnPhotos();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      error(t("settings.profile.invalidFileType"));
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      error(t("settings.profile.fileTooLarge"));
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const completedFields = [
    !!formData.name,
    !!user?.email,
    !!userImage,
    !!formData.gender,
    !!formData.region,
    !!(formData.age || formData.height || formData.size || formData.shoeSize),
    tryOnPhotos.length > 0,
  ].filter(Boolean).length;

  const completionPercent = Math.round((completedFields / 7) * 100);

  return (
    <section className="relative overflow-hidden rounded-2xl border border-border-secondary dark:border-gray-600 bg-white dark:bg-gray-800">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-text-brand/5 via-transparent to-transparent dark:from-indigo-500/15" />
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-text-brand/10 blur-3xl dark:bg-indigo-500/20" />

      <div className="relative flex flex-col gap-5 md:gap-6 p-4 sm:p-5 md:p-7">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <div className="inline-flex items-center gap-1.5 text-xs font-medium text-text-brand dark:text-indigo-400">
              <Sparkles className="h-3.5 w-3.5" />
              {t("settings.profile.personaBadge")}
            </div>
            <h3 className="text-base font-semibold text-text-primary dark:text-gray-100 leading-6">
              {t("settings.profile.identity")}
            </h3>
            <p className="text-sm text-text-tertiary dark:text-gray-400 leading-5 max-w-xl">
              {t("settings.profile.identityDescription")}
            </p>
          </div>

          <div className="flex flex-col items-start sm:items-end gap-1.5 w-full sm:w-auto sm:min-w-[140px]">
            <span className="text-xs font-medium text-text-secondary dark:text-gray-300">
              {t("settings.profile.personaProgress", {
                percent: completionPercent,
              })}
            </span>
            <div className="h-1.5 w-full sm:w-36 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className="h-full rounded-full bg-text-brand dark:bg-indigo-500 transition-all duration-500"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-5 lg:gap-8 items-start">
          <label
            htmlFor="avatar-upload"
            className={cn(
              "group relative mx-auto lg:mx-0 flex flex-col items-center gap-2 sm:gap-3 cursor-pointer",
              isUploadingAvatar && "pointer-events-none opacity-70"
            )}
          >
            <div className="relative">
              <div className="h-20 w-20 sm:h-28 sm:w-28 md:h-32 md:w-32 rounded-full overflow-hidden border-2 border-border-secondary dark:border-gray-500 bg-gray-100 dark:bg-gray-700 shadow-[0_0_0_6px_rgba(0,0,0,0.03)] dark:shadow-[0_0_0_6px_rgba(255,255,255,0.06)] flex items-center justify-center text-xl sm:text-2xl font-semibold text-text-secondary dark:text-gray-200 transition-transform duration-300 group-hover:scale-[1.02]">
                {userImage ? (
                  <img
                    src={userImage}
                    alt={formData.name || t("settings.profile.avatar")}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span>{userInitials}</span>
                )}
              </div>

              <div className="absolute inset-0 rounded-full bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <Camera className="h-6 w-6 text-white" />
              </div>

              <div className="absolute -bottom-0.5 -right-0.5 sm:-bottom-1 sm:-right-1 h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-text-brand dark:bg-indigo-500 text-white flex items-center justify-center shadow-md ring-4 ring-white dark:ring-gray-800">
                <Camera className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm font-medium text-text-primary dark:text-gray-100">
                {isUploadingAvatar
                  ? t("settings.profile.uploading")
                  : t("settings.profile.uploadAvatar")}
              </p>
              <p className="text-xs text-text-tertiary dark:text-gray-400 mt-0.5">
                {t("settings.profile.avatarHint")}
              </p>
            </div>

            <input
              id="avatar-upload"
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/gif, image/svg+xml"
              onChange={handleFileSelect}
              disabled={isUploadingAvatar}
              className="sr-only"
            />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <div className="flex flex-col gap-1.5 md:col-span-2 lg:col-span-1">
              <label className="text-sm font-medium text-text-primary dark:text-gray-200 leading-5">
                {t("settings.profile.fullName")}
                <span className="text-destructive dark:text-red-400 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateFormData("name", e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-border-primary dark:border-gray-600 bg-white dark:bg-gray-900 text-text-primary dark:text-gray-100 text-base leading-6 placeholder:text-text-placeholder dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-text-brand dark:focus:ring-indigo-400 focus:border-transparent transition-all"
                placeholder={t("settings.profile.fullNamePlaceholder")}
              />
            </div>

            <div className="flex flex-col gap-1.5 md:col-span-2 lg:col-span-1">
              <label className="text-sm font-medium text-text-primary dark:text-gray-200 leading-5">
                {t("settings.profile.emailAddress")}
                <span className="text-destructive dark:text-red-400 ml-0.5">*</span>
              </label>
              <input
                type="email"
                value={user?.email || ""}
                disabled
                className="w-full px-3.5 py-2.5 rounded-xl border border-border-primary dark:border-gray-600 bg-gray-50 dark:bg-gray-900/80 text-text-secondary dark:text-gray-300 text-base leading-6 cursor-not-allowed"
              />
            </div>

            <p className="md:col-span-2 text-xs text-text-tertiary dark:text-gray-400 leading-5">
              {t("settings.profile.identityHint")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
